<template>
  <div class="upgrade-apply-management-page">
    <hr class="separator " style="margin-top: 1px"/>

    <div>
      <div class="custom-text">
        <span class="user-manage">{{ $t('UpgradeApplyManagement.title') }}</span>
        
      </div>
      <hr class="separator"/>

    </div>
    <div class="content-container">
      <!-- 搜索功能组件 -->
      <SearchBar @search="handleSearch"/>

      <!-- 升级申请记录表格 -->
      <DataTable :data="tableData" :loading="loading" @refresh="loadData"/>

      <!-- 分页组件 -->
      <div class="pagination">
        <PaginationBar :totalRecords="totalRecords" :current-page="currentPage" @page-change="handlePageChange"
                       @page-size-change="handlePageSizeChange" :page-size="pageSize"/>
      </div>
    </div>
  </div>
</template>

<script>
import SearchBar from './components/SearchBar.vue';
import DataTable from './components/DataTable.vue';
import PaginationBar from '@/components/PaginationBar.vue';
import {getDiffUpgradeApplyList, getUserPermissions} from "@/api/api";

export default {
  components: {
    SearchBar,
    DataTable,
    PaginationBar,
  },
  data() {
    return {
      tableData: [], // 升级申请记录数据
      totalRecords: 0, // 总记录数
      currentPage: 1, // 当前页码
      pageSize: 10,
      isSearching: false,
      searchParams: {},
      loading: false, // 加载状态
      userPermissions: [], // 用户权限
    };
  },
  
  computed: {
    // 其他计算属性可以在这里添加
  },

  created() {
    this.loadData();
  },
  
  mounted() {
    this.loadUserPermissions();
  },
  watch: {
    // 监听语言切换事件
    '$i18n.locale': function () {
      // 语言切换后重新加载表格数据
      if (this.isSearching) {
        this.handleSearch(this.searchParams);
      } else {
        this.loadData();
      }
    },
  },
  methods: {
    // 加载用户权限
    async loadUserPermissions() {
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const cacheKey = `userPermissions_${userId}`;
        
        // 首先尝试从localStorage获取
        const cache = localStorage.getItem(cacheKey) || sessionStorage.getItem(cacheKey);
        if (cache) {
          this.userPermissions = JSON.parse(cache);
        } else {
          // 如果本地没有缓存，则调用API获取权限
          const rawPermissions = await getUserPermissions();
          localStorage.setItem(cacheKey, JSON.stringify(rawPermissions.menus));
          this.userPermissions = rawPermissions.menus;
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
        this.userPermissions = [];
      }
    },
    
    // 检查权限
    hasPermission(permission) {
      if (!this.userPermissions || !Array.isArray(this.userPermissions)) {
        return false;
      }
      
      const hasPermissionRecursive = (permissions) => {
        return permissions.some(perm => {
          if (perm.code === permission) {
            return true;
          }
          if (perm.children && Array.isArray(perm.children)) {
            return hasPermissionRecursive(perm.children);
          }
          return false;
        });
      };
      
      return hasPermissionRecursive(this.userPermissions);
    },
    
    async handleSearch(searchParams) {
      const page = this.currentPage;
      const pageSize = this.pageSize;

      this.isSearching = true;
      this.searchParams = searchParams;
      this.loading = true; // 开始加载
      try {
        const response = await getDiffUpgradeApplyList(page, pageSize, this.searchParams);
        // 在这里进行数据转换 - 适配新的数据库字段
        this.tableData = response.data.map(item => ({
          id: item.id,
          applicantId: item.user_id,
          applyReason: item.content,
          applicantStatus: this.mapStatus(item.status),
          statusText: this.getStatusText(this.mapStatus(item.status)),
          applyTime: item.created_at,
          reviewer: item.deal_user_id,
          reviewTime: item.audit_at,
          reviewComment: item.remark,
          applicantEmail: item.user,
          reviewerEmail: item.deal_user,
        }));

        this.totalRecords = response.meta.total;
        this.currentPage = response.meta.current_page;
        this.pageSize = parseInt(response.meta.per_page);

      } catch (error) {
        console.error(this.$t('ProductManage.searchFailed'));
      } finally {
        this.loading = false; // 加载结束
      }
    },

    async handlePageChange(newPage) {
      // 异步操作的处理逻辑
      try {
        this.currentPage = newPage;
        // 根据新的页码发送请求获取数据，更新tableData
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async handlePageSizeChange(newPageSize) {
      // 异步操作的处理逻辑
      try {
        this.pageSize = newPageSize;
        // 根据新的页码发送请求获取数据，更新tableData
        await this.handleSearch(this.searchParams);
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      }
    },

    async loadData() {
      this.isSearching = false;
      const page = this.currentPage;
      const pageSize = this.pageSize;
      this.loading = true; // 开始加载
      try {
        const data = await getDiffUpgradeApplyList(page, pageSize);
        // 将从服务器获取的数据进行字段重命名 - 适配新的数据库字段
        this.tableData = data.data.map(item => ({
          id: item.id,
          applicantId: item.user_id,
          applyReason: item.content,
          applicantStatus: this.mapStatus(item.status),
          statusText: this.getStatusText(this.mapStatus(item.status)),
          applyTime: item.created_at,
          reviewer: item.deal_user_id,
          reviewTime: item.audit_at,
          reviewComment: item.remark,
          applicantEmail: item.user,
          reviewerEmail: item.deal_user,
        }));

        this.totalRecords = data.meta.total;
        this.currentPage = data.meta.current_page;
        this.pageSize = typeof data.meta.per_page === 'string' ? parseInt(data.meta.per_page, 10) : data.meta.per_page;
      } catch (error) {
        console.error(this.$t('ProductManage.loadDataFailed'));
      } finally {
        this.loading = false; // 加载结束
      }
    },

    // 映射数据库状态到前端状态
    mapStatus(status) {
      const statusMap = {
        0: 'pending',
        1: 'approved',
        2: 'rejected'
      };
      return statusMap[status] || 'pending';
    },

    getStatusText(status) {
      const statusMap = {
        'pending': this.$t('UpgradeApplyManagement.statusPending'),
        'approved': this.$t('UpgradeApplyManagement.statusApproved'),
        'rejected': this.$t('UpgradeApplyManagement.statusRejected')
      };
      return statusMap[status] || status;
    }
  },
};
</script>
<style scoped>
@import "@/assets/css/publicStyle.css";

.upgrade-apply-management-page {
  width: 100%;
  min-height: 0;
  height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.content-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

/* 分页组件样式 */
.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 20px;
}


</style>
